import React, { useId, useState } from "react";
import SourceButton from "./sources/SourceButton";
import { groupRecipients, recipientKey } from "../recipientUtils";
import { RECIPIENT_FIELDS } from "../constants";

const VISIBLE_RECIPIENTS = 4;
const countryNames = new Intl.DisplayNames(["pl"], { type: "region" });
export const countryLabel = (code) =>
  /^[A-Za-z]{2}$/.test(code || "") ? countryNames.of(code.toUpperCase()) : code;

/** Łączy te same dane odbiorcy, zachowując role i wszystkie źródła do porównania. */
export default function RecipientPicker({
  candidates = [],
  recipient,
  roles = {},
  disabled,
  readOnly,
  onSelect,
  onShowSource,
}) {
  const radioGroup = useId();
  const [expanded, setExpanded] = useState(false);
  const recipients = groupRecipients(candidates, roles);
  if (!recipients.length) return null;
  const selectedKey = recipientKey(recipient);
  const visible = expanded
    ? recipients
    : recipients.slice(0, VISIBLE_RECIPIENTS);
  return (
    <section
      id={readOnly ? undefined : "premium-recipients"}
      className="premium-recipient-picker"
      aria-label={readOnly ? "Odbiorcy w dokumentach" : "Wybierz odbiorcę"}
    >
      {!readOnly && (
        <>
          <h3>Wybierz odbiorcę</h3>
          <p>
            Porównaj dane z dokumentów i wybierz, które mają trafić do pola 2
            świadectwa. Wybrany zestaw danych możesz poprawić ręcznie w
            formularzu poniżej.
          </p>
        </>
      )}
      <div className="premium-recipient-grid">
        {visible.map((item) => {
          const selected = item.key === selectedKey;
          return (
            <article
              key={item.key}
              className={`premium-recipient-card${selected ? " selected" : ""}`}
              aria-label={item.name || "Odbiorca bez nazwy"}
            >
              {readOnly ? (
                <strong>{item.name || "Brak nazwy odbiorcy"}</strong>
              ) : (
                <label className="premium-recipient-choice">
                  <strong>{item.name || "Brak nazwy odbiorcy"}</strong>
                  <input
                    type="radio"
                    disabled={disabled}
                    className="form-check-input"
                    name={radioGroup}
                    checked={selected}
                    aria-label={`Wybierz odbiorcę: ${item.name || "bez nazwy"}`}
                    onChange={() =>
                      onSelect(
                        Object.fromEntries(
                          RECIPIENT_FIELDS.map((field) => [
                            field,
                            item[field] || "",
                          ]),
                        ),
                      )
                    }
                  />
                </label>
              )}
              <p className="premium-recipient-address">
                {item.address || "Brak adresu"}
              </p>
              <p>{countryLabel(item.country) || "Brak kraju"}</p>
              <footer className="premium-recipient-footer">
                <h4 className="premium-recipient-sources-title">
                  Źródła danych
                </h4>
                <ul className="premium-recipient-sources">
                  {item.sources.map((source) => (
                    <li key={`${source.role}:${source.location}`}>
                      <strong>{source.role}</strong>
                      <span className="premium-source-location">
                        {source.location}
                        {source.references.length > 0 && (
                          <SourceButton
                            highlighted={source.references.some(
                              (entry) => entry.regions?.length,
                            )}
                            onClick={
                              onShowSource &&
                              (() =>
                                onShowSource(source.references, {
                                  recipient: source.candidateIndex,
                                }))
                            }
                          />
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </footer>
            </article>
          );
        })}
      </div>
      {recipients.length > VISIBLE_RECIPIENTS && (
        <button
          type="button"
          className="btn btn-outline-primary"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded
            ? "Pokaż mniej"
            : `Pokaż pozostałych odbiorców (${recipients.length - VISIBLE_RECIPIENTS})`}
        </button>
      )}
    </section>
  );
}
